'use client';

import type { ColorScheme as ColorSchemeType } from '@/sanity.types';
import type { PortableTextBlock } from 'next-sanity';
import ColorScheme from './colorScheme';
import CustomPortableText from './portable-text';
import Plus from './Plus';

type FaqItem = {
  _key: string;
  question: string;
  answer: PortableTextBlock[];
};

type FaqGroup = {
  _key: string;
  title: string;
  colorScheme?: ColorSchemeType | 'default' | null;
  content?: FaqItem[];
};

const FAQ_SECTION_COLOR_SEQUENCE: ColorSchemeType[] = [
  'grape',
  'tangerine',
  'pea',
  'sky',
  'royal',
  'lilac',
];

function sectionColorScheme(index: number): ColorSchemeType {
  return FAQ_SECTION_COLOR_SEQUENCE[index % FAQ_SECTION_COLOR_SEQUENCE.length];
}

function sectionId(title: string, key: string) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || key;
}

const Accordion = ({ item, name }: { item: FaqItem; name: string }) => (
  <details className="group" name={name}>
    <summary className="cursor-pointer flex gap-4 justify-between list-none [&::-webkit-details-marker]:hidden">
      <span className="body--large">{item.question}</span>
      <span className="group-open:rotate-135 shrink-0 text-(--color-background) transition-transform w-5 h-5">
        <Plus />
      </span>
    </summary>

    <div className="overflow-hidden lg:pr-29.5 pt-5 transition-[height]">
      <CustomPortableText value={item.answer} className="pb-5" />
    </div>
  </details>
);

export default function ImageWithFAQ({
  content,
}: {
  content: FaqGroup[];
  image?: unknown;
}) {
  if (!content?.length) return null;

  return (
    <div className="image-with-faq marketing-container lg:flex lg:gap-15 pb-35">
      <aside className="px-7.5 lg:pr-0 shrink-0 lg:w-100">
        <nav
          aria-label="FAQ sections"
          className="pt-15 max-lg:pb-10 lg:sticky lg:top-28.5 flex flex-col max-lg:items-center lg:items-end gap-2"
        >
          {content.map((group, index) => {
            const id = sectionId(group.title, group._key);
            const scheme = sectionColorScheme(index);

            return (
              <ColorScheme key={group._key} colorScheme={scheme}>
                <a
                  href={`#${id}`}
                  className="inline-block w-fit body max-lg:text-center lg:text-right bg-(--color-background) text-(--color-foreground) py-3 px-4 rounded-[2px] transition-[padding-left] duration-200 ease-in lg:hover:pl-10"
                >
                  {group.title}
                </a>
              </ColorScheme>
            );
          })}
        </nav>
      </aside>

      <div className="grow flex flex-col gap-25 px-7.5 lg:pl-0 max-lg:pt-0 pt-15">
        {content.map((group, index) => {
          const id = sectionId(group.title, group._key);
          const scheme = sectionColorScheme(index);

          return (
            <ColorScheme key={group._key} colorScheme={scheme}>
              <section
                id={id}
                className="faq-section flex flex-col gap-5 scroll-mt-28.5"
              >
                <p className="eyebrow underline decoration-2 decoration-(--color-background) underline-offset-4 w-fit">
                  {group.title}
                </p>
                {group.content?.map((faq) => (
                  <Accordion key={faq._key} item={faq} name="faq-accordion" />
                ))}
              </section>
            </ColorScheme>
          );
        })}
      </div>
    </div>
  );
}
